import { DefaultTheme } from 'styled-components';
import { ComponentSize, paletteDark, palette as paletteLight, size, spacing, typography } from '@mews-ui/theme';
import { dark, light } from '@mewssystems/design-tokens';
import {
    DARK_SEMANTIC_DECISIONS,
    DENSE_TYPOGRAPHY_SCALE,
    getDecisions,
    LIGHT_SEMANTIC_DECISIONS,
    SemanticDecisions,
    TextPresets,
    ThemeOverrides,
} from '@optimus-web/local-tokens';
import { ThemeVariant } from './types';

const getPalette = (themeVariant?: ThemeVariant, overrides?: ThemeOverrides) => {
    let basePalette;
    switch (themeVariant) {
        case ThemeVariant.Light:
            basePalette = paletteLight;
            break;
        case ThemeVariant.Dark:
            basePalette = paletteDark;
            break;
        default:
            basePalette = paletteLight;
            break;
    }

    const finalPalette =
        overrides && overrides.legacyPrimaryColorScale
            ? {
                  ...basePalette,
                  primary: overrides && overrides.legacyPrimaryColorScale,
              }
            : basePalette;
    return finalPalette;
};

const defaultComponentSize: ComponentSize = {
    button: 'medium',
    iconButton: 'medium',
    listItem: { dense: false },
    checkbox: { small: false },
};

interface CreateTheme {
    decisions: SemanticDecisions;
    textPresets: TextPresets;
    themeVariant?: ThemeVariant;
    overrides?: ThemeOverrides;
    tokenOverrides?: Partial<typeof dark | typeof light>;
    componentSize?: Partial<ComponentSize>;
}

export const createTheme = ({
    decisions,
    themeVariant,
    textPresets,
    overrides,
    tokenOverrides = {},
    componentSize,
}: CreateTheme): DefaultTheme => {
    const palette = getPalette(themeVariant, overrides);
    const designTokens = themeVariant === ThemeVariant.Light ? light : dark;
    const finalDesignTokens = {
        ...designTokens,
        ...tokenOverrides,
    };

    return {
        settings: {
            rootSize: 16,
            componentSize: { ...defaultComponentSize, ...componentSize },
        },
        tokens: getDecisions(decisions, overrides),
        palette,
        typography,
        spacing,
        size,
        textPresets,
        designTokens: finalDesignTokens,
    };
};

export const DENSE_LIGHT_THEME = createTheme({
    decisions: LIGHT_SEMANTIC_DECISIONS,
    themeVariant: ThemeVariant.Light,
    textPresets: DENSE_TYPOGRAPHY_SCALE,
});

export const DENSE_DARK_THEME = createTheme({
    decisions: DARK_SEMANTIC_DECISIONS,
    themeVariant: ThemeVariant.Dark,
    textPresets: DENSE_TYPOGRAPHY_SCALE,
});
